/**
 * Phase 21.1g — useLiveNotificationSound
 * --------------------------------------
 * Plays a short notification sound + triggers haptic vibration when a
 * live event arrives (gift / beam request / mod action / live start).
 *
 * - Honors the `rgm.live_sounds_enabled` localStorage preference (on by
 *   default per Phase 21.1g spec; user can toggle in /settings).
 * - First playback inside a user-gesture context unlocks the audio
 *   context on Safari (autoplay policy).
 * - Vibration via navigator.vibrate (Android Chrome / Firefox);
 *   iOS Safari ignores it gracefully.
 */
import { useCallback, useEffect, useRef } from "react";

const LS_KEY = "rgm.live_sounds_enabled";

// Default ON per spec ("enable notification sounds and vibration by
// default for Live activity"). User can toggle off.
export const isLiveSoundEnabled = () => {
  try {
    const v = localStorage.getItem(LS_KEY);
    if (v === null) return true;
    return v === "1" || v === "true";
  } catch (_e) {
    return true;
  }
};

export const setLiveSoundEnabled = (enabled) => {
  try {
    localStorage.setItem(LS_KEY, enabled ? "1" : "0");
  } catch (_e) { /* quota */ }
};

// One-tone bell using the Web Audio API. No external audio file.
const playTone = (ctx, freq, duration, when = 0, type = "sine", gain = 0.08) => {
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, ctx.currentTime + when);
  g.gain.setValueAtTime(0, ctx.currentTime + when);
  g.gain.linearRampToValueAtTime(gain, ctx.currentTime + when + 0.02);
  g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + when + duration);
  osc.connect(g);
  g.connect(ctx.destination);
  osc.start(ctx.currentTime + when);
  osc.stop(ctx.currentTime + when + duration + 0.05);
};

const SOUNDS = {
  // 🎁 gift — three quick ascending chimes
  gift: [
    { f: 880, t: 0.00, d: 0.12, v: 0.07 },
    { f: 1175, t: 0.10, d: 0.12, v: 0.07 },
    { f: 1568, t: 0.20, d: 0.18, v: 0.08 },
  ],
  // 🙋 beam request — friendly two-note "ping" + soft buzz
  beam: [
    { f: 660, t: 0.00, d: 0.14, v: 0.06 },
    { f: 990, t: 0.13, d: 0.20, v: 0.07 },
  ],
  // 🎤 live start — uplifting C-major triad
  live: [
    { f: 523, t: 0.00, d: 0.18, v: 0.05 },
    { f: 659, t: 0.06, d: 0.18, v: 0.05 },
    { f: 784, t: 0.12, d: 0.30, v: 0.07 },
  ],
  // 🔔 default
  default: [
    { f: 880, t: 0.00, d: 0.18, v: 0.06 },
  ],
};

const VIB_PATTERNS = {
  gift: [60, 40, 60],
  beam: [30, 20, 30],
  live: [80],
  default: [40],
};

export const useLiveNotificationSound = () => {
  const ctxRef = useRef(null);
  const lastPlayedRef = useRef(0);

  useEffect(() => {
    // Unlock the AudioContext on the first user interaction so iOS
    // Safari permits playback. We deliberately defer creation until
    // play() is called the first time.
    return () => {
      const ctx = ctxRef.current;
      if (ctx && ctx.state !== "closed") {
        try { ctx.close(); } catch (_e) { /* ignore */ }
      }
      ctxRef.current = null;
    };
  }, []);

  const play = useCallback((kind = "default") => {
    if (!isLiveSoundEnabled()) return;
    // Throttle to avoid audio overlap when many events fire at once.
    const now = Date.now();
    if (now - lastPlayedRef.current < 180) return;
    lastPlayedRef.current = now;
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      if (!ctxRef.current) ctxRef.current = new AudioCtx();
      const ctx = ctxRef.current;
      // Safari sometimes returns "suspended" until a gesture unlocks it.
      if (ctx.state === "suspended") {
        ctx.resume().catch(() => { /* ignore */ });
      }
      const seq = SOUNDS[kind] || SOUNDS.default;
      seq.forEach((s) => playTone(ctx, s.f, s.d, s.t, "sine", s.v));
    } catch (_e) { /* audio unavailable */ }
    try {
      const pattern = VIB_PATTERNS[kind] || VIB_PATTERNS.default;
      if (navigator.vibrate) navigator.vibrate(pattern);
    } catch (_e) { /* not supported */ }
  }, []);

  return { play, isEnabled: isLiveSoundEnabled, setEnabled: setLiveSoundEnabled };
};

export default useLiveNotificationSound;
